import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Minus, Check, Sparkles, ShoppingBag, Share2, ImageDown } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";
import { PRODUCTS } from "@/data/products";
import { Reveal, StaggerItem } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import Stamp from "@/components/Stamp";
import KitStoryCard from "@/components/KitStoryCard";
import Seo, { breadcrumbLd } from "@/components/Seo";
import { PAGE_SEO } from "@/lib/seo";

const tierFor = (count) => (count >= 5 ? 20 : count >= 4 ? 15 : count >= 3 ? 10 : 0);

const BundleBuilder = () => {
  const { t, tr } = useLang();
  const { add } = useCart();
  const [searchParams] = useSearchParams();
  const pickable = useMemo(() => PRODUCTS.filter((p) => !p.soldOut), []);
  const [sel, setSel] = useState(() => {
    const kit = searchParams.get("kit");
    if (!kit) return {};
    const out = {};
    kit.split(",").forEach((pair) => {
      const [id, q] = pair.split(":");
      const prod = PRODUCTS.find((p) => p.id === id);
      if (prod && !prod.soldOut) out[id] = Math.max(1, parseInt(q, 10) || 1);
    });
    return out;
  });
  const [copied, setCopied] = useState(false);
  const [showStory, setShowStory] = useState(false);
  const fromShare = Boolean(searchParams.get("kit"));

  const shareKit = () => {
    const q = Object.entries(sel).map(([id, qty]) => `${id}:${qty}`).join(",");
    const url = `${window.location.origin}/bundle?kit=${encodeURIComponent(q)}`;
    if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const inc = (id) => setSel((s) => ({ ...s, [id]: (s[id] || 0) + 1 }));
  const dec = (id) =>
    setSel((s) => {
      const next = { ...s };
      const q = (next[id] || 0) - 1;
      if (q <= 0) delete next[id];
      else next[id] = q;
      return next;
    });

  const selected = useMemo(
    () => Object.entries(sel).map(([id, qty]) => ({ product: PRODUCTS.find((p) => p.id === id), qty })),
    [sel]
  );
  const count = useMemo(() => selected.reduce((s, i) => s + i.qty, 0), [selected]);
  const gross = useMemo(() => selected.reduce((s, i) => s + i.qty * i.product.price, 0), [selected]);
  const discount = tierFor(count);
  const savings = (gross * discount) / 100;
  const total = gross - savings;

  const nextTierMsg = () => {
    if (count >= 5) return t.builder.maxTier;
    const target = count < 3 ? 3 : count < 4 ? 4 : 5;
    const p = tierFor(target);
    return t.builder.nextTier.replace("{n}", String(target - count)).replace("{p}", String(p));
  };

  const addKit = () => {
    if (count < 3) return;
    const rep = selected[0].product;
    const kit = {
      id: `kit-${Date.now()}`,
      price: Number(total.toFixed(2)),
      image: rep.image,
      images: selected.slice(0, 4).map((s) => s.product.image),
      name: {
        de: `${t.builder.kitName} · ${count} Pieces (−${discount}%)`,
        en: `${t.builder.kitName} · ${count} pieces (−${discount}%)`,
      },
    };
    add(kit, 1);
    setSel({});
  };

  return (
    <div data-testid="page-bundle">
      <Seo {...PAGE_SEO.bundle} jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Kit Builder", path: "/bundle" }])]} />
      <section className="bg-limewash border-b border-ink py-14 md:py-20 relative overflow-hidden">
        <div className="absolute right-6 md:right-20 top-6 hidden sm:block opacity-90">
          <Stamp size={100} bg="bg-cream" />
        </div>
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 relative z-10">
          <Reveal>
            <p className="kicker text-ink/60">{t.builder.kicker}</p>
            <h1 className="display uppercase text-6xl md:text-8xl leading-[0.84] mt-2 text-brand">{t.builder.title}</h1>
            <p className="mt-4 max-w-xl text-lg font-medium text-ink/70">{t.builder.sub}</p>
            <div className="mt-5 inline-flex items-center gap-2 bg-cream border border-ink rounded-full px-4 py-2">
              <Sparkles className="w-4 h-4" strokeWidth={2} />
              <span className="kicker">{t.builder.tierHint}</span>
            </div>
            {fromShare && (
              <p className="mt-4 inline-block kicker bg-lime border border-ink rounded-full px-3 py-1.5 ml-0 sm:ml-3" data-testid="builder-fromshare">
                {t.builder.fromShare}
              </p>
            )}
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-10 md:py-16">
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 grid lg:grid-cols-3 gap-8">
          {/* Picker */}
          <div className="lg:col-span-2">
            <h2 className="display uppercase text-3xl md:text-4xl mb-6">{t.builder.pick}</h2>
            <StaggerItem>
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-px bg-ink border border-ink">
                {pickable.map((p) => {
                  const qty = sel[p.id] || 0;
                  const active = qty > 0;
                  return (
                    <div key={p.id} className={`flex flex-col ${active ? "bg-limewash" : "bg-cream"}`} data-testid={`builder-item-${p.id}`}>
                      <div className="relative aspect-square overflow-hidden border-b border-ink" style={{ backgroundColor: p.tile || "#F9F8F6" }}>
                        <img src={p.image} alt={tr(p.name)} loading="lazy" className="w-full h-full object-cover" />
                        {active && (
                          <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-lime border border-ink flex items-center justify-center">
                            <Check className="w-4 h-4 text-ink" strokeWidth={3} />
                          </span>
                        )}
                      </div>
                      <div className="p-3.5">
                        <p className="display uppercase text-lg leading-none truncate">{tr(p.name)}</p>
                        <div className="flex items-center justify-between mt-2.5">
                          <span className="font-bold">€{p.price}</span>
                          {active ? (
                            <div className="flex items-center gap-1.5">
                              <button onClick={() => dec(p.id)} data-testid={`builder-dec-${p.id}`} className="w-7 h-7 border border-ink rounded-full flex items-center justify-center bg-white hover:bg-lime transition-colors" aria-label="minus">
                                <Minus className="w-3 h-3" strokeWidth={2.5} />
                              </button>
                              <span className="w-6 text-center font-bold" data-testid={`builder-qty-${p.id}`}>{qty}</span>
                              <button onClick={() => inc(p.id)} data-testid={`builder-inc-${p.id}`} className="w-7 h-7 border border-ink rounded-full flex items-center justify-center bg-white hover:bg-lime transition-colors" aria-label="plus">
                                <Plus className="w-3 h-3" strokeWidth={2.5} />
                              </button>
                            </div>
                          ) : (
                            <button onClick={() => inc(p.id)} data-testid={`builder-add-${p.id}`} className="btn-accent h-9 px-3.5 text-xs">
                              <Plus className="w-3 h-3" strokeWidth={2.5} /> Add
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </StaggerItem>
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="card-line bg-white p-6 sticky top-28" data-testid="builder-summary">
              <h2 className="display uppercase text-2xl">{t.builder.yourKit}</h2>

              {selected.length === 0 ? (
                <p className="mt-4 text-ink/50 font-medium">{t.builder.empty}</p>
              ) : (
                <ul className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                  {selected.map(({ product, qty }) => (
                    <li key={product.id} className="flex items-center gap-2 text-sm">
                      <span className="w-5 h-5 rounded-full border border-ink flex-shrink-0" style={{ backgroundColor: product.accent }} />
                      <span className="flex-1 font-medium truncate">{tr(product.name)}</span>
                      <span className="font-bold">×{qty}</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Progress */}
              <div className="mt-5">
                <div className="h-2.5 w-full border border-ink bg-cream rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-lime via-cyan to-pink transition-all duration-300" style={{ width: `${Math.min(100, (count / 5) * 100)}%` }} />
                </div>
                <p className="mt-2 kicker text-ink/60" data-testid="builder-tier-msg">{nextTierMsg()}</p>
              </div>

              <div className="mt-5 pt-4 border-t border-ink space-y-2">
                <div className="flex justify-between font-medium"><span>{t.builder.items}</span><span data-testid="builder-count">{count}</span></div>
                <div className="flex justify-between font-medium"><span>{t.builder.discount}</span><span className="font-bold text-pink" data-testid="builder-discount">−{discount}%</span></div>
                <div className="flex justify-between font-medium"><span>{t.builder.youSave}</span><span className="font-bold">€{savings.toFixed(2)}</span></div>
                <div className="flex justify-between items-end pt-2 border-t border-ink">
                  <span className="display uppercase text-xl">{t.builder.total}</span>
                  <span className="display text-3xl text-brand" data-testid="builder-total">€{total.toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={addKit}
                disabled={count < 3}
                data-testid="builder-add-kit"
                className={`w-full h-14 mt-5 text-base ${count < 3 ? "btn-disabled" : "btn-primary"}`}
              >
                <ShoppingBag className="w-5 h-5" strokeWidth={2} />
                {count < 3 ? t.builder.need3 : t.builder.addKit}
              </button>

              <button
                onClick={shareKit}
                disabled={count === 0}
                data-testid="builder-share"
                className={`w-full h-11 mt-3 text-xs ${count === 0 ? "btn-disabled" : "btn bg-cyan text-ink hover:bg-ink hover:text-cyan"}`}
              >
                {copied ? <Check className="w-4 h-4" strokeWidth={2.5} /> : <Share2 className="w-4 h-4" strokeWidth={2} />}
                {copied ? t.builder.shared : t.builder.share}
              </button>
              {count > 0 && <p className="mt-2 text-xs text-center text-ink/50">{t.builder.shareHint}</p>}

              <button
                onClick={() => setShowStory(true)}
                disabled={count === 0}
                data-testid="builder-story"
                className={`w-full h-11 mt-3 text-xs ${count === 0 ? "btn-disabled" : "btn bg-lilac text-ink hover:bg-ink hover:text-lilac"}`}
              >
                <ImageDown className="w-4 h-4" strokeWidth={2} /> {t.builder.storyBtn}
              </button>
            </div>
          </div>
        </div>
      </section>

      <Marquee text={t.marquee} bg="bg-lilac" color="text-ink" />

      <KitStoryCard
        open={showStory}
        onClose={() => setShowStory(false)}
        selected={selected}
        count={count}
        discount={discount}
        total={total}
      />
    </div>
  );
};

export default BundleBuilder;
